import { useOutletContext } from 'react-router-dom'

import { GetLegalEntityContractSettingsDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import users from 'modules/core/store/collections/users'
import type LegalEntity from 'modules/core/store/models/LegalEntity'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import ContractFields from './ContractFields'
import WeeksPerMonthConfiguration from './_WeeksPerMonthConfiguration'

const ContractsLegalEntityPage = () => {
  const { legalEntity } = useOutletContext<{
    legalEntity: LegalEntity
  }>()

  const {
    company: { id: companyId },
  } = useCurrentSession()

  const { data, loading } = useQuery(GetLegalEntityContractSettingsDocument, {
    variables: {
      legalEntityId: legalEntity.id,
      companyId: companyId,
      contractVersionType: legalEntity.country,
    },
  })

  const weeksPerMonthConfiguration = data?.contracts.weeksPerMonthConfigurationsConnection.nodes[0]
  const canUpsertWeeksPerMonthConfiguration = users.current.policy.can(
    'contracts.weeksPerMonthConfigurationUpsert'
  )
  const contractTemplateId = data?.contracts.contractTemplatesConnection.nodes[0]?.id
  const templateFields = data?.contracts.contractTemplatesConnection.nodes[0]
  const taxonomies = data?.contracts.taxonomies

  const ecctIds = Array.from(
    new Set(
      data?.payroll.expensesCategoriesContractsTaxonomiesConnection.nodes.map(
        (node) => node.contractsTaxonomyId
      )
    )
  )
  const resourceFields = data?.customFields.resourceFieldsConnection.nodes
  const canCreateCustomFields = data?.customFields._policies.companyCreateCustomFields.can || false
  const hasDevCompensations =
    data?.features.featuresConnection.nodes.some(
      (feature) => feature.name === 'dev_compensations' && feature.enabled
    ) ?? false

  return (
    <ResponsivePage.Body loading={loading} error={null} showHeader={false}>
      <ContractFields
        legalEntity={legalEntity}
        templateFields={templateFields}
        taxonomies={taxonomies}
        contractTemplateId={contractTemplateId}
        resourceFields={resourceFields || []}
        canCreateCustomFields={canCreateCustomFields}
        ecctIds={ecctIds}
        hasDevCompensations={hasDevCompensations}
      />
      {canUpsertWeeksPerMonthConfiguration && (
        <WeeksPerMonthConfiguration
          legalEntity={legalEntity}
          weeksPerMonthConfiguration={weeksPerMonthConfiguration}
        />
      )}
    </ResponsivePage.Body>
  )
}

export default ContractsLegalEntityPage
